import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"
import { clearValidationErrors, showValidationError } from "../helpers/notifications"

export default class extends Controller {
  static targets = ["form", "nameInput", "sessionBriefId", "modal"]

  connect() {
    this.boundOnModalShow = this.onModalShow.bind(this)
    this.boundOnBeforeCache = this.onBeforeCache.bind(this)

    if (this.hasModalTarget) {
      this.modalTarget.addEventListener("show.bs.modal", this.boundOnModalShow)
    }

    document.addEventListener("turbo:before-cache", this.boundOnBeforeCache)
  }

  disconnect() {
    if (this.hasModalTarget) {
      this.modalTarget.removeEventListener("show.bs.modal", this.boundOnModalShow)
      this.disposeModal()
    }

    document.removeEventListener("turbo:before-cache", this.boundOnBeforeCache)
  }

  openModal(event) {
    const button = event.currentTarget
    const sessionBriefId = button.dataset.sessionBriefId
    const sessionBriefName = button.dataset.sessionBriefName
    
    this.updateSessionBrief(sessionBriefId, sessionBriefName)
  }

  onModalShow(event) {
    // Clear any previous errors
    clearValidationErrors()
    
    // Focus on the input field when modal opens
    setTimeout(() => {
      if (this.hasNameInputTarget) {
        this.nameInputTarget.focus()
        this.nameInputTarget.select()
      }
    }, 100)
  }

  updateSessionBrief(sessionBriefId, currentName) {
    if (this.hasNameInputTarget) {
      this.nameInputTarget.value = currentName
    }
    
    if (this.hasSessionBriefIdTarget) {
      this.sessionBriefIdTarget.value = sessionBriefId
    }
  }

  submit(event) {
    event.preventDefault()
    
    const formData = new FormData(this.formTarget)
    const sessionBriefId = this.sessionBriefIdTarget.value
    const newName = formData.get("session_brief[name]").trim()
    
    if (!newName) {
      showValidationError("Name cannot be empty")
      return
    }
    
    // Find the submit button properly within the form
    const submitButton = this.formTarget.querySelector('button[type="submit"]')
    const originalText = submitButton ? submitButton.textContent : ""
    
    if (submitButton) {
      submitButton.disabled = true
      submitButton.textContent = "Updating..."
    }
    
    const headers = {
      "Content-Type": "application/json",
      "Accept": "application/json"
    }
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")
    if (csrfToken) headers["X-CSRF-Token"] = csrfToken

    fetch(`/session_briefs/${sessionBriefId}`, {
      method: "PATCH",
      headers,
      body: JSON.stringify({
        session_brief: {
          name: newName
        }
      })
    })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        clearValidationErrors()
        this.updateBriefContainer(sessionBriefId, data.name)
        this.hideModal()
      } else {
        showValidationError(data.error || "Failed to update name")
      }
    })
    .catch(error => {
      console.error("Error:", error)
      showValidationError("An error occurred while updating the name")
    })
    .finally(() => {
      // Re-enable submit button
      if (submitButton && submitButton.parentNode) {
        submitButton.disabled = false
        submitButton.textContent = originalText
      }
    })
  }

  updateBriefContainer(sessionBriefId, newName) {
    const briefContainer = Array.from(this.element.querySelectorAll("[data-session-brief-id]"))
      .find(container => container.dataset.sessionBriefId === String(sessionBriefId))

    if (!briefContainer) return

    briefContainer.querySelectorAll("[data-session-brief-name-display]").forEach(nameDisplay => {
      nameDisplay.textContent = newName
    })

    briefContainer.querySelectorAll("[data-session-brief-name]").forEach(renameButton => {
      renameButton.dataset.sessionBriefName = newName
    })
  }

  hideModal() {
    if (!this.hasModalTarget) return

    const modal = Modal.getInstance(this.modalTarget)
    if (modal) modal.hide()
  }

  onBeforeCache() {
    this.disposeModal()
    clearValidationErrors()
  }

  disposeModal() {
    if (!this.hasModalTarget) return

    const modal = Modal.getInstance(this.modalTarget)
    if (!modal) return

    modal.hide()
    modal.dispose()
  }

}
